/**
 * Dark mode for mail that brought its own colours.
 *
 * A message that styled itself assumed a white canvas -- see the note on
 * `renderHtmlBody` -- and for a long time the answer was to give it one, so a
 * dark app read every newsletter on a white rectangle. Amit asked for better.
 * This rewrites the colours the message declares: every one it names in a
 * `style` attribute, a `<style>` block, or one of the old colour attributes
 * (`bgcolor`, `<font color>`, `<body text link>`) is kept in hue and flipped in
 * lightness, so a white background becomes a dark one, black text becomes
 * light, and a blue button stays a blue button. Images are not touched, which
 * is the residual cost: a logo with white baked into its pixels will sit on a
 * dark card. That is the same trade Gmail and Apple Mail make.
 *
 * # Why rewriting and not `filter: invert()`
 *
 * One rule of `filter: invert(1) hue-rotate(180deg)` on the document, and the
 * same rule again on every image to un-invert it, is the cheap version. It
 * costs every gradient, every image on a coloured card (the card inverts, the
 * photo does not), and every message that was already dark, which comes out
 * bright. Rewriting reads the message's own declarations and answers each one,
 * so a message that was dark stays dark and a photo keeps its background.
 *
 * # What is and is not rewritten
 *
 * Only declaration values, and only for properties that take a colour:
 * `color`, anything ending in `-color`, the `background*`, `border*` and
 * `outline*` families, the shadows, `fill` and `stroke`. A selector, a media
 * query, a `url()`, a font name and a string literal are all left alone -- a
 * newsletter set in "Salmon" is not a newsletter set in pink. Inside such a
 * value the forms recognised are hex (3, 4, 6 and 8 digits), `rgb()`/`rgba()`,
 * `hsl()`/`hsla()` (commas or spaces, `/` alpha), and the 148 named colours.
 * `transparent`, `currentcolor` and the keywords are passed through, because
 * they name no lightness to flip.
 *
 * A sender's own `@media (prefers-color-scheme: dark)` block is switched off
 * (the query is rewritten to one that never matches) and the light block is
 * switched on. That query answers for the operating system's preference, not
 * the app's theme, so honouring it would make the result depend on a setting
 * the person cannot see from here; and a light design flipped by this module
 * and a dark design flipped by this module cannot both be right. One pipeline,
 * predictable output.
 *
 * # The maths, and why it is written the long way
 *
 * Lightness is flipped in HSL, then compressed: `l' = LO + (1 - l) * (HI - LO)`,
 * so pure white lands on a dark grey rather than on black and pure black on an
 * off-white, and mid-tones barely move. Saturation and hue are kept. HSL is a
 * poor model of perception and a very good model of determinism: the same
 * arithmetic in the same order on IEEE doubles gives the same bytes in
 * TypeScript and in Rust, which is what the golden tests in `core/tests/`
 * require. `core/src/darken.rs` is a transliteration of this file, expression
 * for expression -- including the character-by-character scanning in place of
 * regular expressions, which the core does not have and which two engines
 * would not agree on anyway. Change one and change the other.
 *
 * Numbers are rounded with `floor(x * 255 + 0.5)`, spelled out, because the
 * two languages' `round` disagree on halves.
 */

/** Where white lands. Close to the app's dark surface, not black. */
const LO = 0.1;
/** Where black lands. Off-white, so long text is not glare. */
const HI = 0.92;

/**
 * The CSS named colours, all 148, as `name rrggbb` pairs. One string rather
 * than an object literal so that `core/src/darken.rs` can be generated from it
 * and checked against it by `dev/parity.mjs`.
 */
export const NAMED_COLOURS =
  "aliceblue f0f8ff antiquewhite faebd7 aqua 00ffff aquamarine 7fffd4 azure f0ffff beige f5f5dc bisque ffe4c4 " +
  "black 000000 blanchedalmond ffebcd blue 0000ff blueviolet 8a2be2 brown a52a2a burlywood deb887 cadetblue 5f9ea0 " +
  "chartreuse 7fff00 chocolate d2691e coral ff7f50 cornflowerblue 6495ed cornsilk fff8dc crimson dc143c cyan 00ffff " +
  "darkblue 00008b darkcyan 008b8b darkgoldenrod b8860b darkgray a9a9a9 darkgreen 006400 darkgrey a9a9a9 " +
  "darkkhaki bdb76b darkmagenta 8b008b darkolivegreen 556b2f darkorange ff8c00 darkorchid 9932cc darkred 8b0000 " +
  "darksalmon e9967a darkseagreen 8fbc8f darkslateblue 483d8b darkslategray 2f4f4f darkslategrey 2f4f4f " +
  "darkturquoise 00ced1 darkviolet 9400d3 deeppink ff1493 deepskyblue 00bfff dimgray 696969 dimgrey 696969 " +
  "dodgerblue 1e90ff firebrick b22222 floralwhite fffaf0 forestgreen 228b22 fuchsia ff00ff gainsboro dcdcdc " +
  "ghostwhite f8f8ff gold ffd700 goldenrod daa520 gray 808080 green 008000 greenyellow adff2f grey 808080 " +
  "honeydew f0fff0 hotpink ff69b4 indianred cd5c5c indigo 4b0082 ivory fffff0 khaki f0e68c lavender e6e6fa " +
  "lavenderblush fff0f5 lawngreen 7cfc00 lemonchiffon fffacd lightblue add8e6 lightcoral f08080 lightcyan e0ffff " +
  "lightgoldenrodyellow fafad2 lightgray d3d3d3 lightgreen 90ee90 lightgrey d3d3d3 lightpink ffb6c1 " +
  "lightsalmon ffa07a lightseagreen 20b2aa lightskyblue 87cefa lightslategray 778899 lightslategrey 778899 " +
  "lightsteelblue b0c4de lightyellow ffffe0 lime 00ff00 limegreen 32cd32 linen faf0e6 magenta ff00ff maroon 800000 " +
  "mediumaquamarine 66cdaa mediumblue 0000cd mediumorchid ba55d3 mediumpurple 9370db mediumseagreen 3cb371 " +
  "mediumslateblue 7b68ee mediumspringgreen 00fa9a mediumturquoise 48d1cc mediumvioletred c71585 " +
  "midnightblue 191970 mintcream f5fffa mistyrose ffe4e1 moccasin ffe4b5 navajowhite ffdead navy 000080 " +
  "oldlace fdf5e6 olive 808000 olivedrab 6b8e23 orange ffa500 orangered ff4500 orchid da70d6 palegoldenrod eee8aa " +
  "palegreen 98fb98 paleturquoise afeeee palevioletred db7093 papayawhip ffefd5 peachpuff ffdab9 peru cd853f " +
  "pink ffc0cb plum dda0dd powderblue b0e0e6 purple 800080 rebeccapurple 663399 red ff0000 rosybrown bc8f8f " +
  "royalblue 4169e1 saddlebrown 8b4513 salmon fa8072 sandybrown f4a460 seagreen 2e8b57 seashell fff5ee " +
  "sienna a0522d silver c0c0c0 skyblue 87ceeb slateblue 6a5acd slategray 708090 slategrey 708090 snow fffafa " +
  "springgreen 00ff7f steelblue 4682b4 tan d2b48c teal 008080 thistle d8bfd8 tomato ff6347 turquoise 40e0d0 " +
  "violet ee82ee wheat f5deb3 white ffffff whitesmoke f5f5f5 yellow ffff00 yellowgreen 9acd32";

const NAMED: Map<string, string> = (() => {
  const words = NAMED_COLOURS.split(" ");
  const map = new Map<string, string>();
  for (let i = 0; i + 1 < words.length; i += 2) {
    const name = words[i];
    const hex = words[i + 1];
    if (name !== undefined && hex !== undefined) map.set(name, hex);
  }
  return map;
})();

/** The HTML attributes whose whole value is a colour. */
const COLOUR_ATTRIBUTES = new Set(["bgcolor", "color", "bordercolor", "text", "link", "vlink", "alink"]);

// ---- character classes ------------------------------------------------------

function isAlpha(c: string): boolean {
  return (c >= "a" && c <= "z") || (c >= "A" && c <= "Z");
}

function isDigit(c: string): boolean {
  return c >= "0" && c <= "9";
}

function isHex(c: string): boolean {
  return isDigit(c) || (c >= "a" && c <= "f") || (c >= "A" && c <= "F");
}

function isIdentStart(c: string): boolean {
  return isAlpha(c) || c === "-" || c === "_";
}

function isIdentChar(c: string): boolean {
  return isIdentStart(c) || isDigit(c);
}

function isSpace(c: string): boolean {
  return c === " " || c === "\t" || c === "\n" || c === "\r" || c === "\f";
}

/** Case-insensitive `text.startsWith(word, at)` for ASCII `word`. */
function startsCI(text: string, at: number, word: string): boolean {
  if (at + word.length > text.length) return false;
  return text.slice(at, at + word.length).toLowerCase() === word;
}

/** Case-insensitive `text.indexOf(word, from)` for ASCII `word`; -1 if absent. */
function indexOfCI(text: string, word: string, from: number): number {
  const first = word.charAt(0);
  const upper = first.toUpperCase();
  for (let i = from; i + word.length <= text.length; i++) {
    const c = text.charAt(i);
    if ((c === first || c === upper) && startsCI(text, i, word)) return i;
  }
  return -1;
}

// ---- the document -----------------------------------------------------------

/**
 * The message with every colour it declares flipped for a dark canvas.
 *
 * Walks the markup once. `<style>` blocks go through {@link rewriteCss} as
 * stylesheets, every other tag through {@link rewriteTag} for its `style` and
 * colour attributes, `<script>` blocks are copied untouched (the CSP refuses
 * them anyway), and text between tags is never looked at -- a paragraph that
 * says "#ffffff" is prose.
 */
export function darkenColours(html: string): string {
  const n = html.length;
  let out = "";
  let i = 0;
  while (i < n) {
    const c = html.charAt(i);
    if (c === "<") {
      if (startsCI(html, i, "<style") && !isIdentChar(html.charAt(i + 6))) {
        const open = html.indexOf(">", i);
        if (open === -1) {
          out += html.slice(i);
          break;
        }
        out += html.slice(i, open + 1);
        const close = indexOfCI(html, "</style", open + 1);
        const end = close === -1 ? n : close;
        out += rewriteCss(html.slice(open + 1, end));
        i = end;
        continue;
      }
      if (startsCI(html, i, "<script") && !isIdentChar(html.charAt(i + 7))) {
        const close = indexOfCI(html, "</script", i + 7);
        const end = close === -1 ? n : close;
        out += html.slice(i, end);
        i = end;
        continue;
      }
      const next = html.charAt(i + 1);
      if (isAlpha(next) || next === "/") {
        const end = tagEnd(html, i);
        out += rewriteTag(html.slice(i, end + 1));
        i = end + 1;
        continue;
      }
    }
    out += c;
    i++;
  }
  return out;
}

/** Index of the `>` that closes the tag opening at `at`, honouring quotes. */
function tagEnd(html: string, at: number): number {
  let quote = "";
  for (let i = at + 1; i < html.length; i++) {
    const c = html.charAt(i);
    if (quote) {
      if (c === quote) quote = "";
    } else if (c === '"' || c === "'") {
      quote = c;
    } else if (c === ">") {
      return i;
    }
  }
  return html.length - 1;
}

/**
 * One tag, its `style` attribute rewritten as a declaration list and its
 * colour attributes as single colours. Everything else in it is copied.
 *
 * The legacy attributes are lenient the way browsers are: `bgcolor=FFFFFF`
 * with no `#` is a colour, and is written back with one.
 */
function rewriteTag(tag: string): string {
  const n = tag.length;
  let out = "";
  let i = 1;
  // The name, and a leading slash on a closing tag.
  if (tag.charAt(i) === "/") i++;
  while (i < n && isIdentChar(tag.charAt(i))) i++;
  out += tag.slice(0, i);
  while (i < n) {
    const c = tag.charAt(i);
    if (isSpace(c) || c === "/" || c === ">") {
      out += c;
      i++;
      continue;
    }
    if (!isIdentStart(c)) {
      out += c;
      i++;
      continue;
    }
    // An attribute name, then optionally `=` and a value.
    const nameStart = i;
    while (i < n && !isSpace(tag.charAt(i)) && tag.charAt(i) !== "=" && tag.charAt(i) !== ">" && tag.charAt(i) !== "/") i++;
    const name = tag.slice(nameStart, i).toLowerCase();
    let k = i;
    while (k < n && isSpace(tag.charAt(k))) k++;
    if (tag.charAt(k) !== "=") {
      out += tag.slice(nameStart, i);
      continue;
    }
    k++;
    while (k < n && isSpace(tag.charAt(k))) k++;
    let valueStart: number;
    let valueEnd: number;
    let quote = "";
    if (tag.charAt(k) === '"' || tag.charAt(k) === "'") {
      quote = tag.charAt(k);
      valueStart = k + 1;
      valueEnd = tag.indexOf(quote, valueStart);
      if (valueEnd === -1) valueEnd = n;
    } else {
      valueStart = k;
      valueEnd = k;
      while (valueEnd < n && !isSpace(tag.charAt(valueEnd)) && tag.charAt(valueEnd) !== ">") valueEnd++;
    }
    const value = tag.slice(valueStart, valueEnd);
    let replaced = value;
    if (name === "style") {
      replaced = rewriteCss(value);
    } else if (COLOUR_ATTRIBUTES.has(name)) {
      replaced = rewriteAttributeColour(value);
    }
    out += tag.slice(nameStart, valueStart);
    if (quote) {
      out += replaced;
      i = valueEnd;
    } else if (replaced !== value && hasSpace(replaced)) {
      // `rgba(1, 2, 3, .5)` has spaces in it and an unquoted value cannot.
      out += `"${replaced}"`;
      i = valueEnd;
    } else {
      out += replaced;
      i = valueEnd;
    }
  }
  return out;
}

function hasSpace(text: string): boolean {
  for (let i = 0; i < text.length; i++) if (isSpace(text.charAt(i))) return true;
  return false;
}

/** A whole attribute value that should be one colour; unchanged if it is not. */
function rewriteAttributeColour(value: string): string {
  let a = 0;
  let b = value.length;
  while (a < b && isSpace(value.charAt(a))) a++;
  while (b > a && isSpace(value.charAt(b - 1))) b--;
  const inner = value.slice(a, b);
  if (inner === "") return value;
  // Bare hex, the legacy spelling: `bgcolor=FFFFFF`.
  if (inner.charAt(0) !== "#") {
    let allHex = inner.length === 3 || inner.length === 6;
    for (let i = 0; allHex && i < inner.length; i++) if (!isHex(inner.charAt(i))) allHex = false;
    if (allHex) {
      const flipped = flipHex(inner);
      return flipped === null ? value : value.slice(0, a) + flipped + value.slice(b);
    }
  }
  const flipped = rewriteValue(inner);
  return value.slice(0, a) + flipped + value.slice(b);
}

// ---- stylesheets and declaration lists ---------------------------------------

/**
 * Whether a property's value can contain a colour worth flipping.
 *
 * Generous on purpose about the families (`border-radius` will be looked at
 * and contain nothing) and strict about everything else, because the cost of
 * a false positive is a font called Salmon turning pink and the cost of a
 * false negative is one stripe that stays light.
 */
function takesColour(property: string): boolean {
  return (
    property === "color" ||
    property.endsWith("-color") ||
    property.startsWith("background") ||
    property.startsWith("border") ||
    property.startsWith("outline") ||
    property === "box-shadow" ||
    property === "text-shadow" ||
    property === "fill" ||
    property === "stroke" ||
    property === "column-rule" ||
    property === "text-decoration"
  );
}

/**
 * A stylesheet or a declaration list, with colour-bearing values rewritten.
 *
 * The same scanner serves both because a declaration looks the same in either:
 * an identifier, a colon, a value that runs to the next `;`, `}` or `{`. The
 * things around declarations in a stylesheet -- selectors, at-rules, media
 * conditions -- are identifiers *not* followed by a colon, or identifiers whose
 * value is not colour-bearing, and fall through unchanged. `a:hover` is the
 * property `a` with the value `hover`; `(max-width: 600px)` is the property
 * `max-width`; neither takes a colour. Comments and strings are copied whole.
 */
export function rewriteCss(css: string): string {
  const n = css.length;
  let out = "";
  let i = 0;
  while (i < n) {
    const c = css.charAt(i);
    if (c === "/" && css.charAt(i + 1) === "*") {
      const end = css.indexOf("*/", i + 2);
      const stop = end === -1 ? n : end + 2;
      out += css.slice(i, stop);
      i = stop;
      continue;
    }
    if (c === '"' || c === "'") {
      const end = css.indexOf(c, i + 1);
      const stop = end === -1 ? n : end + 1;
      out += css.slice(i, stop);
      i = stop;
      continue;
    }
    if (isIdentStart(c)) {
      let j = i;
      while (j < n && isIdentChar(css.charAt(j))) j++;
      const ident = css.slice(i, j).toLowerCase();
      let k = j;
      while (k < n && isSpace(css.charAt(k))) k++;
      if (css.charAt(k) !== ":") {
        out += css.slice(i, j);
        i = j;
        continue;
      }
      // `prefers-color-scheme: dark` answers for the operating system, not for
      // the app. Off with the sender's dark block, on with its light one.
      if (ident === "prefers-color-scheme") {
        let m = k + 1;
        while (m < n && isSpace(css.charAt(m))) m++;
        let w = m;
        while (w < n && isAlpha(css.charAt(w))) w++;
        const word = css.slice(m, w).toLowerCase();
        if (word === "dark" || word === "light") {
          out += word === "dark" ? "max-width: 0px" : "min-width: 0px";
          i = w;
          continue;
        }
      }
      const valueStart = k + 1;
      const valueEnd = declarationEnd(css, valueStart);
      out += css.slice(i, valueStart);
      const value = css.slice(valueStart, valueEnd);
      out += takesColour(ident) ? rewriteValue(value) : value;
      i = valueEnd;
      continue;
    }
    out += c;
    i++;
  }
  return out;
}

/**
 * Where a value ends: the first `;`, `{`, `}` or unmatched `)` outside quotes
 * and parentheses. The stop character is not consumed.
 */
function declarationEnd(css: string, from: number): number {
  let depth = 0;
  let quote = "";
  for (let i = from; i < css.length; i++) {
    const c = css.charAt(i);
    if (quote) {
      if (c === quote) quote = "";
      continue;
    }
    if (c === '"' || c === "'") quote = c;
    else if (c === "(") depth++;
    else if (c === ")") {
      if (depth === 0) return i;
      depth--;
    } else if (depth === 0 && (c === ";" || c === "{" || c === "}")) return i;
  }
  return css.length;
}

/**
 * One colour-bearing value, every colour in it flipped and everything else --
 * lengths, keywords, `url()`, `!important`, the commas of a gradient --
 * copied. A token that looks like a colour but does not parse is copied too;
 * the message keeps its own mistakes.
 */
function rewriteValue(value: string): string {
  const n = value.length;
  let out = "";
  let i = 0;
  while (i < n) {
    const c = value.charAt(i);
    if (c === "#") {
      let j = i + 1;
      while (j < n && isHex(value.charAt(j))) j++;
      const run = value.slice(i + 1, j);
      const flipped = isIdentChar(value.charAt(j)) ? null : flipHex(run);
      out += flipped ?? value.slice(i, j);
      i = j;
      continue;
    }
    if (c === '"' || c === "'") {
      const end = value.indexOf(c, i + 1);
      const stop = end === -1 ? n : end + 1;
      out += value.slice(i, stop);
      i = stop;
      continue;
    }
    if (isIdentStart(c)) {
      let j = i;
      while (j < n && isIdentChar(value.charAt(j))) j++;
      const ident = value.slice(i, j).toLowerCase();
      if (value.charAt(j) === "(") {
        if (ident === "url") {
          const stop = urlEnd(value, j + 1);
          out += value.slice(i, stop);
          i = stop;
          continue;
        }
        if (ident === "rgb" || ident === "rgba" || ident === "hsl" || ident === "hsla") {
          const close = value.indexOf(")", j + 1);
          if (close !== -1) {
            const flipped = flipFunction(ident, value.slice(j + 1, close));
            if (flipped !== null) {
              out += flipped;
              i = close + 1;
              continue;
            }
          }
        }
        // Any other function -- a gradient, `var()`, `calc()` -- is opened and
        // its arguments scanned in turn, so the colours inside a gradient are
        // flipped and its directions are not.
        out += value.slice(i, j + 1);
        i = j + 1;
        continue;
      }
      const named = NAMED.get(ident);
      const flipped = named === undefined ? null : flipHex(named);
      out += flipped ?? value.slice(i, j);
      i = j;
      continue;
    }
    out += c;
    i++;
  }
  return out;
}

/** Past the `)` that closes a `url(` whose contents start at `from`. */
function urlEnd(value: string, from: number): number {
  const n = value.length;
  let i = from;
  while (i < n && isSpace(value.charAt(i))) i++;
  if (value.charAt(i) === '"' || value.charAt(i) === "'") {
    const end = value.indexOf(value.charAt(i), i + 1);
    i = end === -1 ? n : end + 1;
  }
  const close = value.indexOf(")", i);
  return close === -1 ? n : close + 1;
}

// ---- colours -----------------------------------------------------------------

/**
 * A `#rgb`, `#rgba`, `#rrggbb` or `#rrggbbaa` run (no `#`), flipped and
 * written back as `#rrggbb` or `#rrggbbaa`; null if the run is not one of
 * those lengths.
 */
function flipHex(run: string): string | null {
  let r: number;
  let g: number;
  let b: number;
  let alpha = "";
  if (run.length === 3 || run.length === 4) {
    r = parseInt(run.charAt(0) + run.charAt(0), 16);
    g = parseInt(run.charAt(1) + run.charAt(1), 16);
    b = parseInt(run.charAt(2) + run.charAt(2), 16);
    if (run.length === 4) alpha = (run.charAt(3) + run.charAt(3)).toLowerCase();
  } else if (run.length === 6 || run.length === 8) {
    r = parseInt(run.slice(0, 2), 16);
    g = parseInt(run.slice(2, 4), 16);
    b = parseInt(run.slice(4, 6), 16);
    if (run.length === 8) alpha = run.slice(6, 8).toLowerCase();
  } else {
    return null;
  }
  return toHex(flip(r / 255, g / 255, b / 255)) + alpha;
}

/**
 * The arguments of `rgb()`, `rgba()`, `hsl()` or `hsla()`, flipped; null when
 * they do not parse, in which case the caller keeps the original text.
 *
 * Both syntaxes are read: the comma form and the space form with `/` before
 * the alpha. The alpha, when present, is kept as it was written -- `0.5` or
 * `50%` -- because it is the one part of the colour that is not changing, and
 * reformatting a number is a way of changing it.
 */
function flipFunction(fn: string, args: string): string | null {
  const parts = splitArgs(args);
  if (parts.length !== 3 && parts.length !== 4) return null;
  let rgb: [number, number, number] | null;
  if (fn === "rgb" || fn === "rgba") {
    const r = channel((parts[0] ?? ""));
    const g = channel((parts[1] ?? ""));
    const b = channel((parts[2] ?? ""));
    rgb = r === null || g === null || b === null ? null : [r, g, b];
  } else {
    const h = hue((parts[0] ?? ""));
    const s = fraction((parts[1] ?? ""));
    const l = fraction((parts[2] ?? ""));
    rgb = h === null || s === null || l === null ? null : hslToRgb(h, s, l);
  }
  if (rgb === null) return null;
  const alpha = parts.length === 4 ? (parts[3] ?? "") : "";
  if (alpha !== "" && numberOf(alpha.endsWith("%") ? alpha.slice(0, -1) : alpha) === null) return null;
  const [r, g, b] = flip(rgb[0], rgb[1], rgb[2]);
  if (alpha === "") return toHex([r, g, b]);
  return `rgba(${byte(r)}, ${byte(g)}, ${byte(b)}, ${alpha})`;
}

/** Split on commas, whitespace and `/`, dropping empties. */
function splitArgs(args: string): string[] {
  const parts: string[] = [];
  let current = "";
  for (let i = 0; i < args.length; i++) {
    const c = args.charAt(i);
    if (c === "," || c === "/" || isSpace(c)) {
      if (current !== "") parts.push(current);
      current = "";
    } else {
      current += c;
    }
  }
  if (current !== "") parts.push(current);
  return parts;
}

/**
 * A decimal number, or null. The grammar is narrower than either language's
 * parser -- an optional sign, digits, an optional point, digits, no exponent --
 * and the text is normalised (`+` dropped, `.5` to `0.5`, `5.` to `5.0`) before
 * parsing, so that both languages parse the same string and get the same
 * double.
 */
function numberOf(text: string): number | null {
  let i = 0;
  let sign = "";
  if (text.charAt(i) === "+" || text.charAt(i) === "-") {
    if (text.charAt(i) === "-") sign = "-";
    i++;
  }
  let whole = "";
  while (i < text.length && isDigit(text.charAt(i))) whole += text.charAt(i++);
  let frac = "";
  if (text.charAt(i) === ".") {
    i++;
    while (i < text.length && isDigit(text.charAt(i))) frac += text.charAt(i++);
  }
  if (i !== text.length || (whole === "" && frac === "")) return null;
  return parseFloat(`${sign}${whole === "" ? "0" : whole}.${frac === "" ? "0" : frac}`);
}

/** An `rgb()` channel -- `0`..`255` or a percentage -- as `0`..`1`, clamped. */
function channel(text: string): number | null {
  if (text.endsWith("%")) {
    const p = numberOf(text.slice(0, -1));
    return p === null ? null : clamp01(p / 100);
  }
  const v = numberOf(text);
  return v === null ? null : clamp01(v / 255);
}

/** An `hsl()` saturation or lightness -- a percentage, or a bare number read as one. */
function fraction(text: string): number | null {
  const p = numberOf(text.endsWith("%") ? text.slice(0, -1) : text);
  return p === null ? null : clamp01(p / 100);
}

/** An `hsl()` hue in degrees (a bare number or `deg`), as `0`..`1` around the wheel. */
function hue(text: string): number | null {
  const lower = text.toLowerCase();
  const d = numberOf(lower.endsWith("deg") ? lower.slice(0, -3) : lower);
  if (d === null) return null;
  return (((d % 360) + 360) % 360) / 360;
}

function clamp01(x: number): number {
  return x < 0 ? 0 : x > 1 ? 1 : x;
}

/** The flip itself: hue and saturation kept, lightness inverted and compressed. */
function flip(r: number, g: number, b: number): [number, number, number] {
  const [h, s, l] = rgbToHsl(r, g, b);
  const l2 = LO + (1 - l) * (HI - LO);
  return hslToRgb(h, s, l2);
}

function rgbToHsl(r: number, g: number, b: number): [number, number, number] {
  const max = Math.max(r, Math.max(g, b));
  const min = Math.min(r, Math.min(g, b));
  const l = (max + min) / 2;
  if (max === min) return [0, 0, l];
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h: number;
  if (max === r) h = (g - b) / d + (g < b ? 6 : 0);
  else if (max === g) h = (b - r) / d + 2;
  else h = (r - g) / d + 4;
  return [h / 6, s, l];
}

function hslToRgb(h: number, s: number, l: number): [number, number, number] {
  if (s === 0) return [l, l, l];
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  return [hueToRgb(p, q, h + 1 / 3), hueToRgb(p, q, h), hueToRgb(p, q, h - 1 / 3)];
}

function hueToRgb(p: number, q: number, t: number): number {
  if (t < 0) t += 1;
  if (t > 1) t -= 1;
  if (t < 1 / 6) return p + (q - p) * 6 * t;
  if (t < 1 / 2) return q;
  if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
  return p;
}

/** `0`..`1` to `0`..`255`, rounded half up, the same way in both languages. */
function byte(x: number): number {
  const v = Math.floor(x * 255 + 0.5);
  return v < 0 ? 0 : v > 255 ? 255 : v;
}

function toHex([r, g, b]: [number, number, number]): string {
  return `#${hex2(byte(r))}${hex2(byte(g))}${hex2(byte(b))}`;
}

function hex2(v: number): string {
  return (v < 16 ? "0" : "") + v.toString(16);
}
